import React, { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  KTextInput,
  KButton,
  KCheckbox,
  KSelect,
  KSwitch,
} from "@kyburz/kdesign-system";
import { makeStyles, Theme } from "@material-ui/core";
import useSetState from "../../hooks/useSetState";
import BaseLayout from "../../components/layouts/BaseLayout";
import VehicleTemplateList from "../../components/VehicleTemplateList";
import { createVehicleTemplate } from "../../apiCalls/admin/vehicleTemplates";
import {
  setAlert,
  clearVehicleTemplate,
  setVehicleTemplate,
} from "../../redux/actions/actions";
import { RootStateInterface } from "../../interfaces/ReduxInterface";
import JSONEditor from "../../components/JSONEditor/JSONEditor";

const MemoizedVehicleTemplateList = React.memo(VehicleTemplateList);

const useStyles = makeStyles((theme: Theme) => ({
  buttons: {
    width: "100%",
    height: 72,
    display: "flex",
    justifyContent: "flex-end",
    position: "fixed",
    padding: "16px 48px 0 0",
    bottom: 0,
    right: 0,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    background: "white",
  },
  save: {
    marginLeft: 16,
  },
}));

const NewVehicleTemplate: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();
  const currentTemplate = useSelector(
    (state: RootStateInterface) => state.vehicleTemplate,
  );
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );
  const rows =
    useSelector(
      (state: RootStateInterface) => state.vehicleTemplate.template,
    ) || [];

  const [
    {
      name,
      description,
      defaultTemplate,
      ignitionValue,
      showJsonEditor,
      template,
    },
    setState,
  ] = useSetState({
    name: "",
    description: "",
    defaultTemplate: false,
    ignitionValue: "",
    showJsonEditor: true,
    template: null,
  });

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const handleIsDefaultChange = useCallback(
    (e) => setState({ defaultTemplate: e.target.checked }),
    [],
  );

  const handleCreate = (e) => {
    e.preventDefault();
    const t: [] = template.default_columns;
    const digital = template.is_digital;
    const allColumns = template.all_columns;
    const parsedRows = Object.entries(template.all_columns).map((el: any) => ({
      technicalName: el[0],
      // @ts-ignore
      isDefault: t.includes(el[0]),
      isDigital: digital.includes(el[0]),
      name: el[1].name,
      hasHover: el[1].has_hover,
      hover: el[1].hover,
      default: el[1].default,
    }));
    const defaultColumns = [
      "datetime",
      ignitionValue,
      ...parsedRows
        .filter((el) => el.isDefault)
        .map((el) => el.technicalName)
        .filter((el) => el !== "datetime" && el !== ignitionValue),
    ];

    const isDigital = [
      ...parsedRows.filter((el) => el.isDigital).map((el) => el.technicalName),
    ];

    if (ignitionValue === "") {
      dispatch(setAlert("Ignition status cannot be empty", "error"));
      return;
    }
    createVehicleTemplate(
      currentCustomer?.shortname,
      name,
      description,
      allColumns,
      defaultColumns,
      isDigital,
      ignitionValue,
      defaultTemplate,
    )
      .then(() => {
        dispatch(setAlert(`Template '${name}' created`, "success"));
        dispatch(clearVehicleTemplate());
        history.push("/admin/fleet/templates");
      })
      .catch((err) => {
        dispatch(setAlert(err.message || "An error occurred", "error"));
        console.error(err);
      });
  };

  const handleIgnitionSelect = (el) =>
    setState({ ignitionValue: el?.label || "" });

  const handleJsonEditor = (e) => {
    setState({ [e.target.name]: e.target.checked });
    const t: [] = template.default_columns;
    const digital = template.is_digital;
    dispatch(
      setVehicleTemplate(
        Object.entries(template.all_columns).map((el: any) => ({
          technicalName: el[0],
          // @ts-ignore
          isDefault: t.includes(el[0]),
          isDigital: digital.includes(el[0]),
          name: el[1].name,
          hasHover: el[1].has_hover,
          hover: el[1].hover,
          default: el[1].default,
        })),
      ),
    );
  };

  const onJsonChange = (json: string) => {
    try {
      JSON.parse(json);
    } catch (e) {
      return;
    }
    const parsedJson = JSON.parse(json);
    const t: [] = parsedJson.default_columns;
    setState({
      name: parsedJson.name,
      description: parsedJson.description,
      defaultTemplate: parsedJson.is_default_template,
      // @ts-ignore
      ignitionValue: t?.length >= 1 ? t[1] : "",
    });
  };

  useEffect(() => {
    if (currentTemplate.template.length) {
      const defaultColumns = [
        "datetime",
        ignitionValue,
        ...rows
          .filter((el) => el.isDefault)
          .map((el) => el.technicalName)
          .filter((el) => el !== "datetime" && el !== ignitionValue),
      ];
      const isDigital = [
        ...rows.filter((el) => el.isDigital).map((el) => el.technicalName),
      ];
      const allColumns = Object.assign(
        {},
        ...rows.map((el) => ({
          [el.technicalName]: {
            name: {
              en: el.name.en,
              de: el.name.de,
              fr: el.name.fr,
              it: el.name.it,
            },
            has_hover: el.hasHover,
            hover: {
              en: el.hover.en,
              de: el.hover.de,
              fr: el.hover.fr,
              it: el.hover.it,
            },
            default: el.default,
          },
        })),
      );
      const newTemplate = {
        name,
        description,
        all_columns: allColumns,
        default_columns: defaultColumns,
        is_digital: isDigital,
        ignition_value: ignitionValue,
        is_default_template: defaultTemplate,
      };
      setState({ template: newTemplate });
    }
  }, [currentTemplate, name, description, defaultTemplate, ignitionValue]);

  return (
    <BaseLayout
      title="Create New Vehicle Template"
      handleArrowClick={() => {
        dispatch(clearVehicleTemplate());
        history.push("/admin/fleet/templates");
      }}
    >
      <KSwitch
        label="Show JSON editor"
        onChange={handleJsonEditor}
        checked={showJsonEditor}
        name="showJsonEditor"
        style={{ marginBottom: 16 }}
      />
      {showJsonEditor ? (
        <JSONEditor value={template} jsonChanged={onJsonChange} />
      ) : (
        <>
          <div style={{ display: "flex" }}>
            <KTextInput
              label="Name"
              value={name}
              name="name"
              onChange={handleChange}
              required
              placeholder="DXP Standard Template"
              style={{ marginRight: "16px" }}
            />
            <KSelect
              elements={rows.map((el) => ({
                label: el.technicalName,
                value: el.technicalName,
              }))}
              value={ignitionValue}
              label="Ignition Value"
              style={{ marginRight: "16px" }}
              onSelect={handleIgnitionSelect}
              isClearable={true}
            />
            <KTextInput
              label="Description"
              value={description}
              name="description"
              onChange={handleChange}
              placeholder="Only use this for DXP"
              width="300px"
              style={{ marginRight: "16px" }}
            />
            <KCheckbox
              label="Is Default"
              name="defaultTemplate"
              checked={defaultTemplate}
              handleChange={handleIsDefaultChange}
            />
          </div>
          <div style={{ marginTop: "8px", height: "calc(100% - 200px)" }}>
            <MemoizedVehicleTemplateList />
          </div>
        </>
      )}
      <div className={classes.buttons}>
        <KButton
          children="Cancel"
          theme="secondary"
          onClick={() => {
            dispatch(clearVehicleTemplate());
            history.push("/admin/fleet/templates");
          }}
        />
        <div className={classes.save}>
          <KButton children="Save and close" onClick={handleCreate} />
        </div>
      </div>
    </BaseLayout>
  );
};

export default NewVehicleTemplate;
